import {useCallback} from 'react';
import {useStore} from 'react-redux';
import type {RootState} from '@/store';
import {useLatest} from 'ahooks';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import type {DragDropUploadState} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import {handleDragDropFilesUpload, retryPendingUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import type {ReferenceImageUploadState} from '@Components/ai-prompt-box/libraries/ai-prompt-box-reference-image-upload.library';
import {handleReferenceImageFilesUpload, retryReferenceImageUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-reference-image-upload.library';
import {MAX_ATTACHMENTS} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {getPendingUploadAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {handleDocumentFilesUpload, partitionChatFiles, retryPendingDocumentUpload} from '@Components/ai-prompt-box/libraries/ai-prompt-box-document-upload.library';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';

export interface UseAiPromptBoxDragDropUploader {
  handleFiles: (files: File[]) => number;
  handleReferenceImageFiles: (files: File[]) => void;
  retryUpload: (localId: string) => void;
  showInvalidFileTypeError: VoidFunction;
}

export const useAiPromptBoxDragDropUploader = (id: string): UseAiPromptBoxDragDropUploader => {
  const {state, actions} = useAiPromptBox(id);
  const attachmentsRef = useLatest(state.attachments);
  const store = useStore<RootState>();

  const showInvalidFileTypeError = useCallback((): void => {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t(attachmentsRef.current?.acceptsDocuments ? 'pmwjs_ai_chat_unsupported_file_type_with_documents' : 'pmwjs_ai_chat_unsupported_file_type'),
      showCloseIcon: true,
      interval: 7000,
    });
  }, [attachmentsRef]);

  const getLiveAttachmentCount = useCallback((): number => store.getState().aiPromptBox[id]?.attachments?.items.length ?? 0, [store, id]);

  const buildUploadState = useCallback(
    (): DragDropUploadState => ({
      promptBoxId: id,
      getAttachmentCount: getLiveAttachmentCount,
      setActive: actions.setActiveIfNotProcessing,
      removeImageAttachments: actions.removeImageAttachments,
      addPendingAttachments: actions.addPendingAttachments,
      resolvePendingAttachment: actions.resolvePendingAttachment,
      markPendingAttachmentFailed: actions.markPendingAttachmentFailed,
      markPendingAttachmentUploading: actions.markPendingAttachmentUploading,
      markPendingAttachmentProcessing: actions.markPendingAttachmentProcessing,
    }),
    [id, getLiveAttachmentCount, actions]
  );

  const buildReferenceImageUploadState = useCallback(
    (): ReferenceImageUploadState => ({
      promptBoxId: id,
      getAttachmentCount: getLiveAttachmentCount,
      getMaxAttachments: (): number => attachmentsRef.current?.maxAttachments ?? MAX_ATTACHMENTS,
      setActive: actions.setActiveIfNotProcessing,
      addPendingAttachments: actions.addPendingAttachments,
      resolvePendingAttachment: actions.resolvePendingAttachment,
      markPendingAttachmentFailed: actions.markPendingAttachmentFailed,
      markPendingAttachmentUploading: actions.markPendingAttachmentUploading,
      markPendingAttachmentProcessing: actions.markPendingAttachmentProcessing,
    }),
    [id, attachmentsRef, getLiveAttachmentCount, actions]
  );

  const handleFiles = useCallback(
    (files: File[]): number => {
      const {images, documents, unsupported} = partitionChatFiles(files, attachmentsRef.current?.acceptsDocuments ?? false);
      if (unsupported.length > 0) {
        showInvalidFileTypeError();
      }
      if (images.length > 0) {
        handleDragDropFilesUpload(images, buildUploadState());
      }

      return handleDocumentFilesUpload(documents, buildUploadState());
    },
    [attachmentsRef, buildUploadState, showInvalidFileTypeError]
  );

  const handleReferenceImageFiles = useCallback(
    (files: File[]): void => {
      handleReferenceImageFilesUpload(files, buildReferenceImageUploadState());
    },
    [buildReferenceImageUploadState]
  );

  const retryUpload = useCallback(
    (localId: string): void => {
      const pendingAttachment = getPendingUploadAttachments(attachmentsRef.current?.items).find((item) => item.localId === localId);
      if (!pendingAttachment) {
        return;
      }
      if (pendingAttachment.type === PromptAttachmentType.DOCUMENT) {
        retryPendingDocumentUpload(localId, pendingAttachment, buildUploadState());
      } else if (pendingAttachment.type === PromptAttachmentType.REFERENCE_IMAGE) {
        retryReferenceImageUpload(localId, pendingAttachment, buildReferenceImageUploadState());
      } else {
        retryPendingUpload(localId, pendingAttachment, buildUploadState());
      }
    },
    [attachmentsRef, buildReferenceImageUploadState, buildUploadState]
  );

  return {handleFiles, handleReferenceImageFiles, retryUpload, showInvalidFileTypeError};
};
